import { ReactNode, useState } from "react";
import { Branch } from "@/services/backend/company/types/branch";
import { useListBranchQuery } from "@/services/backend/company/api/branch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { CompanyName } from "@/services/backend/company/components/company-name";
import t from "@/lang/lang";
import { useShowCompanyQuery } from "@/services/backend/company/api/company";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  addSupportingBranchFrom,
  BranchReferencesBase,
  removeBranch,
} from "../types/branch-references-base";
import { BranchRoleBadge } from "./branch-role-badge";

/**
 * BranchReferencesBaseSelect allows selecting branch references.
 * Data for new references will be automatically filled with
 * the data available in the portal.
 *
 * One branch (primary) must always remain. Unselecting the last branch is a no-op.
 * Selecting a supporting branch as primary makes the primary a supporting branch.
 * There must always be one primary branch.
 */
export function BranchReferencesBaseSelect({
  value,
  onValueChange,
  companyId,
  note,
  disabled = false,
}: {
  value: BranchReferencesBase;
  onValueChange: (value: BranchReferencesBase) => void;
  // companyId is single valued and mandatory since
  // BranchReferencesBase is in the context of a document
  // which has a single-valued, mandatory reference to a company.
  companyId: UUID;
  note?: ReactNode;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data: list } = useListBranchQuery({
    company: { active: true, values: [companyId] },
  });
  const selection = value.branches ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="justify-between"
          disabled={disabled}
        >
          {selection.length > 0 ? (
            selection
              .map(
                (ref) =>
                  list?.branches.find((c) => c.id === ref.branchId)?.name,
              )
              .join(", ")
          ) : (
            <span className="text-muted-foreground font-normal">
              {t("Niederlassungen auswählen ...")}
            </span>
          )}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        {note && (
          <div className="text-muted-foreground border-b p-2 text-xs">
            {note}
          </div>
        )}
        <Command>
          <CommandInput placeholder={t("Suchen")} className="h-9" />
          <CommandEmpty>{t("Nichts gefunden")}</CommandEmpty>
          <CommandGroup>
            {list?.branches.map((branch) => (
              <BranchCommandItem
                key={branch.id}
                value={value}
                onValueChange={(v) => {
                  onValueChange(v);
                  setOpen(false);
                }}
                branch={branch}
              />
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function BranchCommandItem({
  value,
  onValueChange,
  branch,
}: {
  value: BranchReferencesBase;
  onValueChange: (v: BranchReferencesBase) => void;
  branch: Branch;
}) {
  const { data: company } = useShowCompanyQuery({ id: branch.companyId });

  if (!company) return null;

  return (
    <CommandItem
      key={branch.id}
      value={`${branch.name} ${company.shortName}`}
      onSelect={() => {
        if (value.branches.some((ref) => ref.branchId === branch.id)) {
          onValueChange(removeBranch(value, branch.id));
        } else {
          onValueChange(addSupportingBranchFrom(value, branch));
        }
      }}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          value.branches.some((ref) => ref.branchId === branch.id)
            ? "opacity-100"
            : "opacity-0",
        )}
      />
      <span>
        {branch.name} (
        <CompanyName companyId={branch.companyId} short />)
      </span>
      <BranchRoleBadge
        className="ml-auto"
        role={
          value.branches.find((ref) => ref.branchId === branch.id)?.branchRole
        }
      />
    </CommandItem>
  );
}
